import React from 'react';
import { formatDisplayDate, formatPrice, slotLabel } from '../../../utils/labTestUtils';
import Icon from '../../Icon';

export default function OrderSummary({
  selectedPatient,
  selectedSku,
  selectedSlot,
  appointmentDate,
  address,
  walletBalanceMinorUnits,
  insufficientWalletBalance,
}) {
  const checklist = [
    { label: 'Patient selected', done: !!selectedPatient },
    { label: 'Test plan chosen', done: !!selectedSku },
    { label: 'Slot confirmed',   done: !!selectedSlot },
  ];

  return (
    <aside className="lab-booking-aside fade-in-up delay-2">
      <div className="lab-summary-card">
        <div className="lab-summary-header">
          <h3>Order summary</h3>
          <p>Complete each step to book a home collection</p>
        </div>

        <div className="lab-summary-body">
          <ul className="lab-summary-checklist">
            {checklist.map((c) => (
              <li key={c.label} className={c.done ? 'is-done' : ''}>
                <span>{c.done ? '✓' : '○'}</span> {c.label}
              </li>
            ))}
          </ul>

          <div className="lab-summary-row">
            <div className="lab-summary-icon"><Icon name="person" size={16} /></div>
            <div>
              <div className="lab-summary-label">Patient</div>
              <div className={`lab-summary-value${!selectedPatient ? ' is-muted' : ''}`}>
                {selectedPatient
                  ? `${selectedPatient.firstName} ${selectedPatient.lastName}`
                  : 'Not selected'}
              </div>
              {selectedPatient && (
                <div className="lab-summary-value is-muted" style={{ fontSize: 11, marginTop: 2 }}>
                  {selectedPatient.phone || 'No phone'}
                </div>
              )}
            </div>
          </div>

          <div className="lab-summary-row">
            <div className="lab-summary-icon"><Icon name="vial" size={16} /></div>
            <div>
              <div className="lab-summary-label">Test plan</div>
              <div className={`lab-summary-value${!selectedSku ? ' is-muted' : ''}`}>
                {selectedSku ? selectedSku.name : 'Not selected'}
              </div>
              {selectedSku && (
                <div className="lab-summary-value is-muted" style={{ fontSize: 11, marginTop: 2 }}>
                  {selectedSku.noOfTestsIncluded ?? selectedSku.testsIncluded?.length ?? 0} tests
                </div>
              )}
            </div>
          </div>

          <div className="lab-summary-row">
            <div className="lab-summary-icon"><Icon name="calendar" size={16} /></div>
            <div>
              <div className="lab-summary-label">Appointment</div>
              <div className={`lab-summary-value${!selectedSlot ? ' is-muted' : ''}`}>
                {selectedSlot
                  ? `${formatDisplayDate(appointmentDate)} · ${slotLabel(selectedSlot)}`
                  : appointmentDate
                    ? formatDisplayDate(appointmentDate)
                    : 'Pick date & slot'}
              </div>
            </div>
          </div>

          {address?.pincode && (
            <div className="lab-summary-row">
              <div className="lab-summary-icon"><Icon name="pin" size={16} /></div>
              <div>
                <div className="lab-summary-label">Collection address</div>
                <div className="lab-summary-value">
                  {[address.addressLine1, address.city].filter(Boolean).join(', ')}
                </div>
                <div className="lab-summary-value is-muted" style={{ fontSize: 11, marginTop: 2 }}>
                  PIN {address.pincode}
                </div>
              </div>
            </div>
          )}
        </div>

        {selectedSku && walletBalanceMinorUnits !== null && (
          <div
            className="lab-summary-row"
            style={{
              background: insufficientWalletBalance ? 'var(--red-bg)' : 'transparent',
              borderRadius: 'var(--radius-sm)',
              padding: insufficientWalletBalance ? '8px 10px' : 0,
              marginTop: insufficientWalletBalance ? 8 : 0,
            }}
          >
            <div className="lab-summary-icon"><Icon name="wallet" size={16} /></div>
            <div>
              <div className="lab-summary-label">Patient wallet balance</div>
              <div
                className="lab-summary-value"
                style={{ color: insufficientWalletBalance ? 'var(--red)' : undefined, fontWeight: 700 }}
              >
                ₹{(walletBalanceMinorUnits / 100).toLocaleString('en-IN')}
              </div>
              {insufficientWalletBalance && (
                <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 2, lineHeight: 1.4 }}>
                  <Icon name="alert" size={14} /> Not enough to cover this order. Ask the patient to top up first.
                </div>
              )}
            </div>
          </div>
        )}

        {selectedSku && (
          <div className="lab-summary-total">
            <div className="lab-summary-total-label">Total</div>
            <div className="lab-summary-total-amount">{formatPrice(selectedSku.rate)}</div>
          </div>
        )}
      </div>
    </aside>
  );
}
